import { useState, useEffect, useMemo } from "react";
import { downloadAmbulanceCoverage, downloadAmbulanceCoverageRepostLocally, fetchDocumentList, getCompany } from "../../../Http/Index";

const useDownloadHook = () => { 
    const [selectedCompany, setSelectedCompany] = useState<string | number>("");
    const [companies, setCompanies] = useState<any[]>([]);
    const [documentList, setDocumentList] = useState<any>({});
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());

    useEffect(() => {  
        const fetchCompanies = async () => {        
            try {
                const response = await getCompany(); 
                const data = response.data; 
                setCompanies(data.data); 
            }
            catch (error) {
                console.error("Error fetching companies:", error);
            }
        }
        const fetchDocument = async (page = 1, pageLimit = 10) => {
            try {
                const response = await fetchDocumentList(page, pageLimit);
                const data = response.data;
                setDocumentList(data.data);
            }
            catch (error) {
                console.error("Error fetching document list:", error);
            }
        }
        fetchCompanies();
        fetchDocument(currentPage, limit);
    }, [currentPage, limit]); 
    
    const filteredCompanies = useMemo(() => {
        return companies.filter(company => 
            company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [companies, searchTerm]);
    
    const selectedCompanyName = companies.find(c => c._id === selectedCompany)?.companyName || "";
    
    const handlePageChange = (page: number) => {
        const totalPages = documentList?.pagination?.totalPages || 1;
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };
    
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };


      const parseFileName = (fileName: string) => {
            const parts = fileName.split('_');
            const name = fileName;
            const companyId = parts[2];
            const timestamp = parts[3];
            const date = timestamp ? new Date(parseInt(timestamp)).toLocaleString() : '';
            
            return { name, companyId, timestamp: date };
        };
    
        const handleDownload = async (fileName: string) => {
            setDownloadingFiles(prev => new Set(prev).add(fileName));
            try {
                const response = await downloadAmbulanceCoverageRepostLocally("ambulance-coverage-reports", fileName);
                
                // Create blob from stream response
                const blob = new Blob([response.data], {
                    type: (response.headers['content-type'] as string | undefined) || 'application/octet-stream'
                });
                
                // Create download link
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                
                // Trigger download
                document.body.appendChild(link);
                link.click();
                
                // Cleanup
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error("Error downloading file:", error);
            } finally {
                setDownloadingFiles(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(fileName);
                    return newSet;
                });
            }
        };
    
        const fetchDocument = async (page = 1, pageLimit = 10) => {
            try {
                const response = await fetchDocumentList(page, pageLimit);
                const data = response.data;
                setDocumentList(data.data);
            }
            catch (error) {
                console.error("Error fetching document list:", error);
            }
        }

        const handleInitiateDownload = async() => {
            if (!selectedCompany) {
                alert("Please select a company to generate the report.");
                return;
            }
            alert("The download process has been initiated")
            setIsGenerating(true);
            try{
                const company = companies.find(c => c._id === selectedCompany);
               let response = await downloadAmbulanceCoverage(String(selectedCompany),{companyName:company?.companyName || ""});
               alert(response?.data?.message);
               // Refresh document list after successful generation
               await fetchDocument(currentPage, limit);
            }catch(error){
                console.error("Error downloading ambulance coverage:", error);
            } finally {
                setIsGenerating(false);
            }
        };

    return {
        selectedCompany,
        setSelectedCompany,
        companies,
        documentList,
        currentPage,
        limit,
        handlePageChange,
        handleLimitChange,
        handleDownload,
        parseFileName,
        handleInitiateDownload,
        searchTerm,
        setSearchTerm,
        isOpen,
        setIsOpen,
        filteredCompanies,
        selectedCompanyName,
        isGenerating,
        downloadingFiles
    }
}   

export default useDownloadHook;